// Fiat-wallet module parser — converts admin-service snake_case wire DTOs
// to camelCase view-model types consumed everywhere else. Only parsed
// data is allowed past this file.

import type {
  FiatTransactionDto,
  FiatTransactionsResponseDto,
  FiatWalletDto,
  FiatWalletsResponseDto,
} from './walletContracts'
import type {
  FiatTransaction,
  FiatTransactionsResponse,
  FiatWallet,
  FiatWalletsResponse,
  FiatWalletTransactionReason,
  FiatWalletTransactionStatus,
  FiatWalletTransactionType,
} from '../types/walletTypes'

export const parseFiatTransaction = (dto: FiatTransactionDto): FiatTransaction => ({
  id: String(dto.id ?? ''),
  investorId: String(dto.investor_id ?? ''),
  fiatWalletId: String(dto.fiat_wallet_id ?? ''),
  transactionType: dto.transaction_type as FiatWalletTransactionType,
  transactionReason: dto.transaction_reason as FiatWalletTransactionReason,
  amount: String(dto.amount ?? '0'),
  currencyCode: String(dto.currency_code ?? ''),
  transactionStatus: dto.transaction_status as FiatWalletTransactionStatus,
  utrNumber: dto.utr_number ?? null,
  balanceBefore: dto.balance_before ?? null,
  balanceAfter: dto.balance_after ?? null,
  orderId: dto.order_id ?? null,
  rentalPeriodId: dto.rental_period_id ?? null,
  idempotencyKey: dto.idempotency_key ?? null,
  transactionDocument: dto.transaction_document ?? null,
  createdAt: String(dto.created_at ?? ''),
  createdBy: dto.created_by ?? null,
})

export const parseFiatTransactionsResponse = (
  dto: FiatTransactionsResponseDto,
): FiatTransactionsResponse => ({
  rows: (dto.rows ?? []).map(parseFiatTransaction),
  total: Number(dto.total ?? 0),
})

export const parseFiatWallet = (dto: FiatWalletDto): FiatWallet => {
  const investorRaw = dto.investor ?? null
  return {
    id: String(dto.id ?? ''),
    investorId: String(dto.investorId ?? dto.investor_id ?? ''),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    balance: String(dto.balance ?? '0'),
    availableBalance: dto.availableBalance ?? dto.available_balance ?? null,
    status: String(dto.status ?? ''),
    createdAt: String(dto.createdAt ?? dto.created_at ?? ''),
    updatedAt: String(dto.updatedAt ?? dto.updated_at ?? ''),
    investor: investorRaw
      ? {
          id: String(investorRaw.id ?? ''),
          firstName: String(investorRaw.firstName ?? investorRaw.first_name ?? ''),
          lastName: String(investorRaw.lastName ?? investorRaw.last_name ?? ''),
          email: investorRaw.email ?? null,
        }
      : null,
  }
}

export const parseFiatWalletsResponse = (dto: FiatWalletsResponseDto): FiatWalletsResponse => ({
  rows: (dto.rows ?? []).map(parseFiatWallet),
  rowsCount: Number(dto.rowsCount ?? dto.total ?? 0),
})
